import { useTranslation } from 'react-i18next'
import { MIRROR_SOURCE } from './faqOptions'

const DOT = {
  synced: 'bg-emerald-500',
  pending: 'bg-amber-400',
  syncing: 'bg-amber-400',
  failed: 'bg-red-500',
}

const th = 'border-b border-slate-100 px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500'
const td = 'px-4 py-3 align-top text-sm text-slate-700'

// One page of FAQ entries. `selection` (a Set of ids) and its checkboxes
// exist only for users who can write; selection is page-scoped.
export default function FaqList({
  items, total, page, pageSize, maxEntries, statusTotal,
  canWrite, showUsage, selection, onSelectionChange, onPageChange, onOpen, onDelete,
}) {
  const { t, i18n } = useTranslation()
  const number = (value) => Number(value).toLocaleString(i18n.language)
  const first = total === 0 ? 0 : Math.min((page - 1) * pageSize + 1, total)
  const last = Math.min(page * pageSize, total)
  const label = (kind, value) => t(`faq.${kind}.${value}`, { defaultValue: value || '—' })
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const allSelected = items.length > 0 && items.every((item) => selection.has(item.id))

  const toggleAll = () => {
    onSelectionChange(allSelected ? new Set() : new Set(items.map((item) => item.id)))
  }
  const toggle = (id) => {
    const next = new Set(selection)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onSelectionChange(next)
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: 760 }}>
          <thead>
            <tr className="bg-slate-50">
              {canWrite && (
                <th scope="col" className={`${th} w-10`}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label={t('faq.list.selectAll')} />
                </th>
              )}
              <th scope="col" className={th}>{t('faq.list.columns.question')}</th>
              <th scope="col" className={th}>{t('faq.list.columns.answer')}</th>
              <th scope="col" className={th}>{t('faq.list.columns.group')}</th>
              <th scope="col" className={th}>{t('faq.list.columns.source')}</th>
              {showUsage && <th scope="col" className={th}>{t('faq.list.columns.usage')}</th>}
              <th scope="col" className={`${th} text-right`}><span className="sr-only">{t('faq.row.actions')}</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-slate-100 last:border-b-0">
                {canWrite && (
                  <td className={td}>
                    <input
                      type="checkbox"
                      disabled={item.source === MIRROR_SOURCE}
                      checked={selection.has(item.id)}
                      onChange={() => toggle(item.id)}
                      aria-label={`${t('faq.list.selectRow')}: ${item.question}`}
                    />
                  </td>
                )}
                <td className={`${td} font-medium text-slate-900`}>
                  <span className="flex items-start gap-2">
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[item.sync_status] || 'bg-slate-300'}`}
                      title={label('syncStatus', item.sync_status)}
                      aria-label={label('syncStatus', item.sync_status)}
                      role="img"
                    />
                    <span className="break-words">{item.question}</span>
                  </span>
                </td>
                <td className={`${td} whitespace-pre-line break-words`}>{item.answer}</td>
                <td className={td}>{item.group?.name || t('faq.ungrouped')}</td>
                <td className={td}>{label('sources', item.source)}</td>
                {showUsage && <td className={td}>{item.usage_7d == null ? '—' : number(item.usage_7d)}</td>}
                <td className={`${td} whitespace-nowrap text-right`}>
                  <button
                    type="button"
                    onClick={() => onOpen(item)}
                    aria-label={`${t(canWrite && item.source !== MIRROR_SOURCE ? 'faq.row.edit' : 'faq.row.view')}: ${item.question}`}
                    className="font-medium text-[#1535c8]"
                  >
                    {t(canWrite && item.source !== MIRROR_SOURCE ? 'faq.row.edit' : 'faq.row.view')}
                  </button>
                  {canWrite && item.source !== MIRROR_SOURCE && (
                    <button
                      type="button"
                      onClick={() => onDelete(item)}
                      aria-label={`${t('faq.row.delete')}: ${item.question}`}
                      className="ml-3 font-medium text-red-600"
                    >
                      {t('faq.row.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-600 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>{t('faq.list.showing', { first: number(first), last: number(last), total: number(total) })}</span>
          {statusTotal != null && (
            <span className="text-slate-400">
              {t('faq.list.counter', { total: number(statusTotal), max: number(maxEntries) })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="h-9 rounded-lg border border-slate-200 px-3 font-medium disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('faq.list.previous')}
          </button>
          <span className="text-xs text-slate-500">
            {t('faq.list.page', { page: number(page), pages: number(pageCount) })}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            className="h-9 rounded-lg border border-slate-200 px-3 font-medium disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('faq.list.next')}
          </button>
        </div>
      </div>
    </section>
  )
}
